import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Eye, MousePointerClick, Activity, TrendingUp, Globe, Smartphone, Monitor, Tablet } from "lucide-react";
import { toast } from "sonner";

/* First-party analytics dashboard.
   Everything here reads from /api/admin/analytics/* — no third-party JS,
   no external SaaS. Data is written by /api/track/{visit,event} and lives
   in the local Mongo (`page_views` and `events` collections with 365-d TTL). */

const DEVICE_ICON = { mobile: Smartphone, tablet: Tablet, desktop: Monitor, unknown: Globe };

function fmt(n) {
  if (n === null || n === undefined) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000)    return `${(n / 1000).toFixed(1)}k`;
  return n.toLocaleString();
}

function Sparkline({ points, height = 40, color = "#2dd4d8" }) {
  if (!points || points.length === 0) return null;
  const w = 260;
  const h = height;
  const max = Math.max(...points, 1);
  const step = w / Math.max(1, points.length - 1);
  const d = points.map((v, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${(h - (v / max) * (h - 4) - 2).toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} className="mt-2" data-testid="analytics-sparkline">
      <path d={d} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function KpiCard({ label, value, hint, Icon, testid, sparklinePoints, suffix }) {
  const rendered = (value === null || value === undefined) ? "—" : `${fmt(value)}${suffix || ""}`;
  return (
    <Card className="bg-[#0e1726] border-slate-800" data-testid={testid}>
      <CardContent className="pt-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
            <div className="text-3xl font-extrabold text-slate-100 tracking-tight mt-1" data-testid={`${testid}-value`}>
              {rendered}
            </div>
            {hint ? <div className="text-xs text-slate-500 mt-1">{hint}</div> : null}
          </div>
          {Icon ? <Icon className="w-5 h-5 text-teal-400/80" /> : null}
        </div>
        {sparklinePoints ? <Sparkline points={sparklinePoints} /> : null}
      </CardContent>
    </Card>
  );
}

function FunnelBar({ step, prev, biggest }) {
  const width = biggest ? Math.max(4, Math.min(100, (step.count / biggest) * 100)) : 0;
  // Conversion is always relative to the previous step and is clamped 0-100 so
  // a step ever showing > 100% (which would be a data bug — every downstream
  // step is a strict subset of its parent by construction) can't render as
  // a wider bar than its parent.
  // When the previous step is 0, "0%" reads as "no conversion" but is really
  // "no denominator". Render an em-dash instead so a downstream step showing
  // 1 with an upstream of 0 doesn't look like a broken funnel.
  const conv = prev
    ? (prev.count === 0 ? null : Math.min(100, Math.round((step.count / prev.count) * 100)))
    : 100;
  const chipTone = conv === null
    ? "text-slate-400 bg-slate-500/10"
    : conv >= 40
      ? "text-emerald-300 bg-emerald-500/10"
      : conv >= 15
        ? "text-amber-300 bg-amber-500/10"
        : "text-rose-300 bg-rose-500/10";
  return (
    <div className="flex items-center gap-3">
      <div className="w-40 text-sm text-slate-300 shrink-0">{step.label}</div>
      <div className="flex-1 h-8 bg-slate-800/60 rounded-md overflow-hidden relative">
        <div
          className="h-full bg-gradient-to-r from-teal-500/40 to-teal-400/60 border-r border-teal-300/40"
          style={{ width: `${width}%` }}
        />
        <div className="absolute inset-0 flex items-center justify-between px-3">
          <span className="text-xs font-mono text-slate-100 font-semibold">{fmt(step.count)}</span>
          {prev ? (
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${chipTone}`}>
              {conv === null ? "—" : `${conv}%`}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [summary, setSummary]     = useState(null);
  const [series, setSeries]       = useState(null);
  const [pages, setPages]         = useState(null);
  const [referrers, setReferrers] = useState(null);
  const [devices, setDevices]     = useState(null);
  const [sims, setSims]           = useState(null);
  const [funnel, setFunnel]       = useState(null);
  const [countries, setCountries] = useState(null);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [s, t, p, r, d, sm, f, c] = await Promise.all([
          api.get("/api/admin/analytics/summary"),
          api.get("/api/admin/analytics/timeseries?days=30"),
          api.get("/api/admin/analytics/top-pages?days=7&limit=15"),
          api.get("/api/admin/analytics/top-referrers?days=30&limit=10"),
          api.get("/api/admin/analytics/devices?days=30"),
          api.get("/api/admin/analytics/sims?days=30"),
          api.get("/api/admin/analytics/funnel?days=30"),
          api.get("/api/admin/analytics/countries?days=30"),
        ]);
        if (cancelled) return;
        setSummary(s.data);
        setSeries(t.data);
        setPages(p.data);
        setReferrers(r.data);
        setDevices(d.data);
        setSims(sm.data);
        setFunnel(f.data);
        setCountries(c.data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const sparkUniques = series?.days?.map((d) => d.uniques) || [];
  const sparkViews   = series?.days?.map((d) => d.pageviews) || [];
  const biggestStep  = funnel?.steps?.[0]?.count || 0;

  return (
    <div className="space-y-8" data-testid="admin-analytics">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-100">Visitor analytics</h1>
        <p className="text-sm text-slate-500 mt-1">
          First-party, cookieless. Data lives in your database — never shared with third parties.
        </p>
      </div>

      {loading && !summary ? (
        <div className="text-center py-24 text-slate-500 text-sm">Loading analytics…</div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard label="Unique visitors · 24 h"  value={summary?.uniques?.day}     Icon={Users}       testid="kpi-uniques-day" />
            <KpiCard label="Unique visitors · 7 d"   value={summary?.uniques?.week}    Icon={Users}       testid="kpi-uniques-week"  sparklinePoints={sparkUniques} />
            <KpiCard label="Pageviews · 7 d"         value={summary?.pageviews?.week}  Icon={Eye}         testid="kpi-pageviews-week" sparklinePoints={sparkViews} />
            <KpiCard label="Bounce rate · 7 d"       value={summary?.bounce_rate_week_pct}
                     hint={`${fmt(summary?.sessions_week)} sessions`}
                     Icon={MousePointerClick} testid="kpi-bounce-week" suffix="%" />
          </div>

          {/* Funnel */}
          <Card className="bg-[#0e1726] border-slate-800" data-testid="analytics-funnel">
            <CardHeader className="pb-2">
              <CardTitle className="text-slate-100 text-lg flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-teal-400" /> Conversion funnel · last 30 days
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2.5 pt-1">
              {(funnel?.steps || []).map((step, i, arr) => (
                <FunnelBar key={step.key} step={step} prev={i === 0 ? null : arr[i - 1]} biggest={biggestStep} />
              ))}
              {!funnel?.steps?.length && (
                <div className="text-sm text-slate-500 py-6">No funnel data yet.</div>
              )}
            </CardContent>
          </Card>

          {/* Two-column: top pages + top referrers */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="bg-[#0e1726] border-slate-800" data-testid="analytics-top-pages">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-100 text-lg">Top pages · 7 d</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="divide-y divide-slate-800/70">
                  {(pages?.pages || []).map((p) => (
                    <div key={p.path} className="flex items-center justify-between py-2 text-sm">
                      <span className="text-slate-300 font-mono text-[13px] truncate mr-3" title={p.path}>{p.path}</span>
                      <span className="text-slate-500 shrink-0"><span className="text-slate-200 font-semibold">{fmt(p.pageviews)}</span> · {fmt(p.uniques)} u</span>
                    </div>
                  ))}
                  {!pages?.pages?.length && <div className="text-sm text-slate-500 py-6">No pageviews yet.</div>}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0e1726] border-slate-800" data-testid="analytics-top-referrers">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-100 text-lg">Top referrers · 30 d</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="divide-y divide-slate-800/70">
                  {(referrers?.referrers || []).map((r) => (
                    <div key={r.referrer} className="flex items-center justify-between py-2 text-sm">
                      <span className="text-slate-300 truncate mr-3" title={r.referrer}>{r.referrer}</span>
                      <span className="text-slate-200 font-semibold shrink-0">{fmt(r.visits)}</span>
                    </div>
                  ))}
                  {!referrers?.referrers?.length && <div className="text-sm text-slate-500 py-6">No external referrers yet.</div>}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sim usage */}
          <Card className="bg-[#0e1726] border-slate-800" data-testid="analytics-sims">
            <CardHeader className="pb-2">
              <CardTitle className="text-slate-100 text-lg flex items-center gap-2">
                <Activity className="w-4 h-4 text-teal-400" /> Simulator usage · last 30 days
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                {(sims?.sims || []).slice(0, 20).map((s) => {
                  const rate = s.opens > 0 ? Math.round((s.completes / s.opens) * 100) : 0;
                  return (
                    <div key={s.sim_id} className="flex items-center justify-between py-1.5 border-b border-slate-800/50 text-sm">
                      <span className="text-slate-300 truncate mr-3">{s.sim_name}</span>
                      <span className="text-slate-500 shrink-0 font-mono">
                        <span className="text-slate-200">{fmt(s.opens)}</span> opens · <span className="text-slate-200">{fmt(s.completes)}</span> ✓ · <span className={rate >= 40 ? "text-emerald-400" : rate >= 15 ? "text-amber-400" : "text-rose-400"}>{rate}%</span>
                      </span>
                    </div>
                  );
                })}
                {!sims?.sims?.length && <div className="text-sm text-slate-500 py-6">No sim events yet.</div>}
              </div>
            </CardContent>
          </Card>

          {/* Devices + Countries */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="bg-[#0e1726] border-slate-800" data-testid="analytics-devices">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-100 text-lg">Devices · browsers · 30 d</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Device</div>
                  <div className="flex flex-wrap gap-2">
                    {(devices?.devices || []).map((d) => {
                      const Icon = DEVICE_ICON[d.key] || Globe;
                      return (
                        <span key={d.key} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-slate-700 bg-slate-800/50 text-slate-200">
                          <Icon className="w-3.5 h-3.5" /> <span className="capitalize">{d.key}</span> · <span className="font-mono">{fmt(d.n)}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Browser</div>
                  <div className="flex flex-wrap gap-2">
                    {(devices?.browsers || []).map((b) => (
                      <span key={b.key} className="text-xs px-2.5 py-1.5 rounded-md border border-slate-700 bg-slate-800/50 text-slate-200">
                        {b.key} · <span className="font-mono">{fmt(b.n)}</span>
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0e1726] border-slate-800" data-testid="analytics-countries">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-100 text-lg flex items-center gap-2">
                  <Globe className="w-4 h-4 text-teal-400" /> Top countries · 30 d
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="divide-y divide-slate-800/70">
                  {(countries?.countries || []).map((c) => (
                    <div key={c.country} className="flex items-center justify-between py-2 text-sm">
                      <span className="text-slate-300 font-mono">{c.country}</span>
                      <span className="text-slate-200 font-semibold">{fmt(c.visits)}</span>
                    </div>
                  ))}
                  {!countries?.countries?.length && (
                    <div className="text-sm text-slate-500 py-6">
                      No country data yet — appears once the app is behind a CDN that adds a country header (e.g. Cloudflare).
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
